import {
  Box,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import React, { useState } from "react";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import { YieldEstimate } from "../../../../../../../../API/calibration";
import { uncertaintyRate } from "../utils/yield-error";
import {
  getLocaleString,
  formatCountRange,
  formatTonnesRange,
} from "../utils/yield-format";

const KG_TO_TONNES_RATE = 0.001;

export function YieldCard({
  lastCalibratedDate,
  yieldEstimate,
  manualCountsCount,
  averageAppleCountPerTree,
}: {
  lastCalibratedDate: string;
  yieldEstimate?: YieldEstimate;
  manualCountsCount: number;
  averageAppleCountPerTree: number;
}) {
  const [yieldCardExpanded, setYieldCardExpanded] = useState<boolean>(true);
  const { t, i18n } = useTranslation();

  if (!yieldEstimate) return null;

  const rate = uncertaintyRate(manualCountsCount);
  const lowerLimitRate = 1 - rate;
  const higherLimitRate = 1 + rate;

  const calibratedCount =
    (yieldEstimate.yield_estimate_kg / yieldEstimate.average_weight_g) * 1000;

  const countLower =
    yieldEstimate.yield_estimate_kg_lower != null
      ? (yieldEstimate.yield_estimate_kg_lower /
          yieldEstimate.average_weight_g) *
        1000
      : null;
  const countUpper =
    yieldEstimate.yield_estimate_kg_upper != null
      ? (yieldEstimate.yield_estimate_kg_upper /
          yieldEstimate.average_weight_g) *
        1000
      : null;

  return (
    <Box
      sx={{
        position: "absolute",
        display: { xs: "none", sm: "none", md: "block" },
        "@media (orientation: portrait)": {
          display: "none",
        },
        bottom: { sm: 280, md: 65 },
        right: { xs: 26, md: 110 },
        left: { xs: 16, md: "auto" },
        zIndex: 20,
        pointerEvents: "none",
      }}
    >
      <Card
        sx={{
          minWidth: { xs: "auto", md: 280 },
          maxWidth: { xs: "none", md: 320 },
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
          borderRadius: 3,
          bgcolor: "var(--boxed-background)",
          backdropFilter: "blur(8px)",
          pointerEvents: "auto",
        }}
      >
        <CardContent sx={{ pb: "16px !important" }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={yieldCardExpanded ? 2 : 0}
            sx={{ cursor: "pointer" }}
            onClick={() => setYieldCardExpanded(!yieldCardExpanded)}
          >
            <Typography
              variant="subtitle2"
              fontWeight="700"
              color="text.secondary.dark"
              sx={{
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              {t("header.calibrate.estimated_field_yield")}
            </Typography>
            <IconButton size="small" edge="end">
              {yieldCardExpanded ? <ExpandLess /> : <ExpandMoreIcon />}
            </IconButton>
          </Stack>
          <Collapse in={yieldCardExpanded} timeout="auto" unmountOnExit>
            <Stack spacing={2} mt={2}>
              <Box>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-end"
                  mb={0.5}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary.dark"
                    fontWeight={600}
                  >
                    {t("text.calibrate.total_apple_count")}
                  </Typography>
                </Stack>
                <Typography
                  variant="h6"
                  fontWeight="700"
                  color="text.secondary.dark"
                >
                  {getLocaleString(calibratedCount, i18n.language)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {`${t("text.calibrate.expected_range")}: ${formatCountRange(countLower, countUpper, calibratedCount, lowerLimitRate, higherLimitRate, i18n.language)}`}
                </Typography>
              </Box>
              <Box>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-end"
                  mb={0.5}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary.dark"
                    fontWeight={600}
                  >
                    {t("text.calibrate.average_apple_count_per_tree")}
                  </Typography>
                </Stack>
                <Typography
                  variant="h6"
                  fontWeight="700"
                  color="text.secondary.dark"
                >
                  {getLocaleString(averageAppleCountPerTree, i18n.language)}
                </Typography>
              </Box>
              <Box>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-end"
                  mb={0.5}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary.dark"
                    fontWeight={600}
                  >
                    {t("text.calibrate.yield_hectare")}
                  </Typography>
                </Stack>

                <Box mb={1}>
                  <Typography
                    variant="h6"
                    fontWeight="700"
                    color="text.secondary.dark"
                    sx={{ lineHeight: 1.2 }}
                  >
                    {`${yieldEstimate.yield_estimate_kg_per_ha !== undefined ? getLocaleString(yieldEstimate.yield_estimate_kg_per_ha * KG_TO_TONNES_RATE, i18n.language, 2) : "N/A"} ${t("text.calibrate.tonnes_hectare")}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {`${t("text.calibrate.expected_range")}: ${
                      yieldEstimate.yield_estimate_kg_per_ha !== undefined
                        ? formatTonnesRange(
                            yieldEstimate.yield_estimate_kg_per_ha_lower,
                            yieldEstimate.yield_estimate_kg_per_ha_upper,
                            yieldEstimate.yield_estimate_kg_per_ha,
                            lowerLimitRate,
                            higherLimitRate,
                            i18n.language,
                            KG_TO_TONNES_RATE,
                          )
                        : "N/A"
                    }`}
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-end"
                  mb={0.5}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary.dark"
                    fontWeight={600}
                  >
                    {t("text.calibrate.yield.scanned_area")}
                  </Typography>
                </Stack>

                <Box mb={1}>
                  <Typography
                    variant="h6"
                    fontWeight="700"
                    color="text.secondary.dark"
                    sx={{ lineHeight: 1.2 }}
                  >
                    {`${yieldEstimate.yield_estimate_kg !== undefined ? getLocaleString(yieldEstimate.yield_estimate_kg * KG_TO_TONNES_RATE, i18n.language, 2) : "N/A"} ${t("text.calibrate.yield.tonnes")}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {`${t("text.calibrate.expected_range")}: ${
                      yieldEstimate.yield_estimate_kg !== undefined
                        ? formatTonnesRange(
                            yieldEstimate.yield_estimate_kg_lower,
                            yieldEstimate.yield_estimate_kg_upper,
                            yieldEstimate.yield_estimate_kg,
                            lowerLimitRate,
                            higherLimitRate,
                            i18n.language,
                            KG_TO_TONNES_RATE,
                          )
                        : "N/A"
                    }`}
                  </Typography>
                </Box>
              </Box>
              {lastCalibratedDate && (
                <Box mt={3} pt={2} borderTop={1} borderColor="divider">
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <InfoOutlined fontSize="inherit" />
                    {t("label.calibrated_from_manual_counts_on", {
                      date: DateTime.fromISO(lastCalibratedDate)
                        .setLocale(i18n.language)
                        .toLocaleString(DateTime.DATE_FULL),
                    })}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Collapse>
        </CardContent>
      </Card>
    </Box>
  );
}
